<script lang="ts">
	import { transformInfiniteDataPublishing } from '../../../../utils/transformInfiniteDataPublishing';
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { BooksGalleryItem } from '#lib/features/publishing/components/BooksGalleryItem/index.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		artistSectionQueryKey,
		getArtistBooks,
	} from '#lib/websites/artists/api/getArtistSections.js';
	import {
		ARTIST_PROJECTS_LIMIT,
		PUBLISHING_BOOKS_SORT,
	} from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import {
		GetArtistPagePublishingBooksDocument,
		type GetArtistPagePublishingBooksQuery,
	} from '#lib/websites/artists/queries/__generated__/getArtistPagePublishingBooks.generated.js';
	import { ShopRoutes } from '#lib/websites/shop/constants/routes.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { hasMoreInfiniteItems } from '$global/utils/infinite-loading/hasMoreInfiniteItems';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import { shouldRestoreInfiniteItems } from '$global/utils/infinite-loading/shouldRestoreInfiniteItems';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();
	let dataCyPrefix = $derived(`${dataCy}-publishing`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let booksResponse = $derived(
		page.data.booksResponse as ArtistsPageData['booksResponse']
	);
	let booksVariables = $derived(
		page.data.booksVariables as ArtistsPageData['booksVariables']
	);
	let count = $derived(
		booksResponse?.Publishing_Books_aggregated[0]?.countDistinct?.id || 0
	);
	let viewAllHref = $derived(
		`${ShopRoutes.All}?category=Books&category_type=3&artist=${encodeURIComponent(
			artist.name || ''
		)}&artist_type=3`
	);

	// Branches on the data the route's own loader produced rather than on the
	// flag: this component is shared, and only the loader knows which path ran.
	let useRest = $derived('restArtistSlug' in page.data);
	let slug = $derived((page.data.restArtistSlug ?? '') as string);

	// Books are the one section queried from the Publishing_Books side, so its
	// page envelope carries the count as well as the rows. Cast to the document
	// type for the same reason as the other sections.
	let query = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: artistSectionQueryKey('books', slug, {
						limit: ARTIST_PROJECTS_LIMIT,
						offset: 0,
					}),
					queryFn: async (offset: number) => {
						const data = await getArtistBooks(undefined, slug, {
							limit: ARTIST_PROJECTS_LIMIT,
							offset,
						});
						return {
							Publishing_Books_aggregated: [
								{ countDistinct: { id: data.total } },
							],
							Publishing_Books: data.items,
						} as unknown as GetArtistPagePublishingBooksQuery;
					},
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
			: infiniteQuery({
					gqlClient,
					variables: { ...booksVariables, sort: PUBLISHING_BOOKS_SORT },
					document: GetArtistPagePublishingBooksDocument,
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
	);

	let books = $derived(
		getInfiniteData({
			query,
			transform: transformInfiniteDataPublishing,
		})
	);

	// Use server-side data immediately, fall back to infinite query data once loaded
	let displayItems = $derived(
		books && books.length > 0
			? books
			: transformInfiniteDataPublishing(
					booksResponse as unknown as GetArtistPagePublishingBooksQuery
				)
	);

	let hasMore = $derived(
		hasMoreInfiniteItems({
			query,
			totalCount: count,
			loadedCount: displayItems?.length || 0,
			limit: ARTIST_PROJECTS_LIMIT,
			getPageRows: (queryPage) => queryPage.Publishing_Books,
		})
	);

	const handleLoadMoreBooks = () => {
		setArtistSectionCount(
			'books',
			(displayItems?.length || 0) + ARTIST_PROJECTS_LIMIT
		);
		query.fetchNextPage();
	};

	// After back/forward navigation the TanStack cache usually still holds the
	// previously loaded pages; when it has been garbage-collected, catch back
	// up to the count persisted in the history entry page by page.
	$effect(() => {
		if (
			shouldRestoreInfiniteItems({
				query,
				targetCount: getArtistSectionCount('books'),
				loadedCount: displayItems?.length || 0,
			})
		) {
			query.fetchNextPage();
		}
	});
</script>

{#if displayItems && displayItems.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="BOOKS"
			href={viewAllHref}
			dataCy={dataCyPrefix}
			label="View all books"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each displayItems as book}
				<BooksGalleryItem {dataCy} {book} />
			{/each}
		</div>
		{#if hasMore}
			<Button
				dataCy={`${dataCyPrefix}-load-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				loading={query.isFetchingNextPage}
				onclick={handleLoadMoreBooks}>Show more</Button
			>
		{/if}
	</div>
{/if}
